<template>
  <div>
    <b-table
      v-if="entitlements.length"
      ref="refEntitlementsTable"
      class="position-relative event_list shadow-table"
      :class="{ low_items: entitlements.length < 4 }"
      :items="entitlements"
      responsive
      :fields="tableColumns"
      primary-key="_id"
      :sort-by.sync="sortBy"
      show-empty
      no-sort-reset
      empty-text="No matching records found"
      :sort-desc.sync="isSortDirDesc"
      :per-page="perPage"
      :current-page="currentPage"
    >
      <!-- Column: Role -->
      <template #cell(stringID)="data">
        <div class="text-nowrap">
          <b-button
            v-if="canViewThisAction('show', 'Entitlement')"
            v-ripple.400="'rgba(30, 30, 30, 0.15)'"
            variant="link"
            class="weight-600 align-middle detail-view-id"
            :to="{ name: 'customers-entitlements-show', params: { id: data.item._id } }"
          >
            {{ data.item.stringID }}
          </b-button>
          <b-button
            v-else
            v-ripple.400="'rgba(30, 30, 30, 0.15)'"
            variant="none"
            class="text-bold-black align-middle detail-view-id not-button"
          >
            {{ data.item.stringID }}
          </b-button>
        </div>
      </template>

      <template #cell(name)="data">
        {{ data.item.name }}
        <feather-icon
          v-if="data.item.schemeItemID"
          v-b-modal.terms-condition-modal
          icon="InfoIcon"
          size="16"
          class="ml-25 mt-25 text-muted cursor-pointer"
          @click="openTermsModal(data.item)"
        />
      </template>

      <template #cell(fileID)="data">
        <div class="text-nowrap">
          <b-button
            v-if="canViewThisAction('show', 'File')"
            v-ripple.400="'rgba(30, 30, 30, 0.15)'"
            variant="link"
            class="weight-600 align-middle detail-view-id"
            :to="{ name: 'customers-files-show', params: { id: data.item.fileID.fileID } }"
          >
            {{ data.item.fileID.fileNo }}
          </b-button>
          <b-button
            v-else
            v-ripple.400="'rgba(30, 30, 30, 0.15)'"
            variant="link"
            class="text-bold align-middle detail-view-id not-button"
          >
            {{ data.item.fileID.fileNo }}
          </b-button>
        </div>
      </template>

      <template #cell(schemeID)="data">
        <div class="text-nowrap">
          <b-button
            v-if="canViewThisAction('show', 'EntitlementScheme')"
            v-ripple.400="'rgba(30, 30, 30, 0.15)'"
            variant="link"
            class="weight-600 align-middle detail-view-id"
            :to="{ name: 'products-schemes-show', params: { id: data.item.schemeID._id } }"
          >
            {{ data.item.schemeID.stringID }}
          </b-button>
          <b-button
            v-else
            v-ripple.400="'rgba(30, 30, 30, 0.15)'"
            variant="link"
            class="text-bold align-middle detail-view-id not-button"
          >
            {{ data.item.schemeID.stringID }}
          </b-button>
          <p>{{ data.item.schemeID.name }}</p>
        </div>
      </template>

      <template #cell(validUntil)="data">
        <div
          v-if="data.item.lifeTime"
          class="text-nowrap"
        >
          <span class="">Life Time</span>
        </div>
        <div
          v-else-if="data.item.validUntil"
          class="text-nowrap"
        >
          <span class="">{{ dateFormat(data.item.validUntil) }}</span>
        </div>
        <div v-else>
          -
        </div>
      </template>

      <template #cell(createdBy)="data">
        <div class="text-nowrap">
          <span>{{ data.item.createdBy.name }}</span>
          <br>
          <span class="">{{ dateFormatWithTime(data.item.createdAt) }}</span>
        </div>
      </template>

      <!-- Column: Status -->
      <template #cell(status)="data">
        <b-badge
          :variant="`light-${resolveEntitlementStatusVariant(data.item.status)}`"
          class="text-capitalize"
        >
          {{ data.item.status }}
        </b-badge>
        <small
          v-if="data.item.statusType"
          class="text-muted d-block"
        >
          <template v-if="data.item.statusType === 'service_request' && data.item.statusReferenceID">
            <div class="d-flex">
              <span class="mr-25">via</span>
              <b-button
                v-if="canViewThisAction('show', 'ServiceRequest')"
                :to="{ name: 'operation-service-requests-show', params: { id: data.item.statusReferenceID } }"
                class="font-weight-bold p-0"
                variant="link"
              >
                {{ data.item.statusReference }}
              </b-button>
              <span v-else>
                {{ data.item.statusReference }}
              </span>
            </div>
          </template>

          <template v-else>
            {{ resolveStatusTypeLabel(data.item.statusType, data.item.statusReference) }}
          </template>
        </small>
      </template>

      <!-- Column: Actions -->
      <template
        v-if="canViewThisAction('show', 'Entitlement')"
        #cell(actions)="data"
      >
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >

          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item
            v-if="canViewThisAction('undo-redemption', 'Entitlement') && data.item.status == 'redeemed'"
            @click="undoRedemption(data.item)"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Undo Redemption</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canViewThisAction('redeem', 'Entitlement') && data.item.status == 'redeemable'"
            @click="proceedToRedeem(data.item._id)"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Redeem</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canViewThisAction('mark-as-redeemable', 'Entitlement') && data.item.status == 'cancelled'"
            @click="markAsRedeemable(data.item)"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Mark as Redeemable</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canViewThisAction('mark-as-redeemed', 'Entitlement') && (data.item.status == 'redeemable' || data.item.status == 'cancelled')"
            @click="markAsRedeemed(data.item)"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Mark as Redeemed</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canViewThisAction('mark-as-cancelled', 'Entitlement') && data.item.status == 'redeemable'"
            @click="markAsCancelled(data.item)"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Mark as Cancelled</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canViewThisAction('send-email', 'Entitlement')"
            @click="sendEmail(data.item)"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Re-send Email</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canViewThisAction('update', 'Entitlement') && (data.item.status == 'redeemable' || data.item.status == 'expired')"
            id="toggle-btn"
            v-b-modal.modal-update-validuntil-all-entitlement
            @click="setEntitlement(data.item)"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit Validity</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canViewThisAction('update', 'Entitlement') && data.item.status == 'redeemable' && data.item.schemeItemID != null && data.item.schemeItemID.type == 1"
            @click="openEditSelectedOption(data.item)"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit Selected Option</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canViewThisAction('update', 'Entitlement')"
            id="toggle-btn"
            v-b-modal.modal-update-remarks-all-entitlement
            @click="setEntitlementRemarks(data.item)"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit Internal Remarks</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template #bottom-row="">
        <td
          colspan="10"
        >
          <b-row>
            <b-col
              md="2"
              sm="2"
              class="my-25"
            >
              <b-form-group
                class="mb-0"
              >
                <label class="d-inline-block text-sm-left mr-50 weight-400 fs-14">Per page</label>
                <b-form-select
                  id="perPageSelect"
                  v-model="perPage"
                  size="sm"
                  :options="pageOptions"
                  class="w-50"
                />
              </b-form-group>
            </b-col>
            <b-col
              md="10"
              sm="10"
              class="my-25"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                align="right"
                prev-text="Prev"
                next-text="Next"
              />
            </b-col>
          </b-row>
        </td>
      </template>
    </b-table>
    <div
      v-else
      class="empty_block"
    >
      <h4>No entitlements found.</h4>
    </div>

    <b-modal
      id="modal-update-validuntil-all-entitlement"
      ref="update-status-modal"
      modal-class="status-update-modal"
      cancel-variant="flat-primary"
      ok-variant="success"
      ok-title="Update"
      cancel-title="Cancel"
      centered
      no-close-on-backdrop
      title="Edit Validity"
      @ok="submitForm"
    >
      <b-form @submit.prevent="submitForm">
        <validation-observer
          ref="entitlementValidUntilEditForm"
        >
          <b-form-group class="regular-label">
            <div>
              <div
                class="d-flex"
                style="gap: 10px;"
              >
                <div style="width: 50%;">
                  <!-- Validity Year -->
                  <validation-provider
                    #default="{ errors }"
                    name="Validity Year"
                    vid="validityYear"
                  >
                    <v-select
                      id="h-scheme-validityYear"
                      v-model="validityYear"
                      label="title"
                      :options="validityYears"
                      :reduce="title => title.code"
                      :clearable="false"
                      :disabled="lifeTime"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </div>
                <div style="width: 50%;">
                  <!-- Validity Type -->
                  <validation-provider
                    #default="{ errors }"
                    name="Validity Type"
                    vid="validityType"
                  >
                    <v-select
                      id="h-scheme-validityType"
                      v-model="validityType"
                      label="title"
                      :options="validityTypes"
                      :reduce="title => title.code"
                      :clearable="false"
                      :disabled="lifeTime"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </div>
              </div>
            </div>
            <div
              class="flex-grow-1 d-flex justify-content-start"
              style="padding-top:8px;"
            >
              <b-form-checkbox
                v-model="lifeTime"
                class="mt-0"
                style="white-space: nowrap;"
              >
                Lifetime
              </b-form-checkbox>
            </div>
            <small class="text-muted-999">
              From date of issuance, as assigned when this entitlement is issued to a customer. Customer will have this period of time to redeem/consume this entitlement.
            </small>
          </b-form-group>
        </validation-observer>
      </b-form>
    </b-modal>
    <b-modal
      id="modal-update-remarks-all-entitlement"
      ref="update-remarks-modal"
      modal-class="status-update-modal"
      cancel-variant="flat-primary"
      ok-variant="success"
      ok-title="Save Changes"
      cancel-title="Cancel"
      centered
      no-close-on-backdrop
      title="Edit Internal Remarks"
      @ok="submitRemarksForm"
    >
      <b-form @submit.prevent="submitRemarksForm">
        <validation-observer
          ref="entitlementRemarksEditForm"
        >
          <b-form-group
            label-for="h-entitlement-status"
          >
            <validation-provider
              #default="{ errors }"
              name="Status"
              vid="status"
              rules="required"
            >
              <b-form-textarea
                id="h-remarks"
                v-model="internalRemarks"
                placeholder="Internal Remarks"
                :state="(errors.length > 0 || remarksValidation) ? false : null"
                name="remarks"
                rows="3"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="remarksValidation"
                class="text-danger"
              >
                {{ remarksError }}
              </small>
            </validation-provider>
          </b-form-group>
        </validation-observer>
      </b-form>
    </b-modal>
    <b-modal
      id="terms-condition-modal"
      centered
      hide-footer
      title="Terms and Conditions"
      modal-class="terms-condition-modal"
    >
      <div v-if="selectedEntitlement">
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="terms-condition-content"
          v-html="selectedEntitlement.schemeItemID.termsCondition"
        />
      </div>
    </b-modal>
    <b-modal
      id="modal-redemption-code"
      ref="redemption-code-modal"
      centered
      hide-footer
      modal-class="redemption-code-modal"
      no-close-on-backdrop
      @hide="redemptionCode = ['','','','','','','','']; redemptionCodeError = ''"
    >
      <template #modal-header>
        <button
          type="button"
          class="close"
          @click="$bvModal.hide('modal-redemption-code')"
        >
          ×
        </button>
      </template>
      <div class="text-center redemption-wrapper">

        <!-- Gift Icon -->
        <div class="gift-icon-wrapper">
          <feather-icon
            icon="GiftIcon"
            size="80"
            class="gift-icon"
          />
        </div>

        <!-- Title -->
        <h3 class="redemption-title">
          Enter Redemption Code
        </h3>
        <!-- Description -->
        <p class="redemption-description">
          This code can be generated only by customer using their Nirvana SG customer app for verification purposes.
        </p>

        <!-- Code Inputs -->
        <div class="redemption-code-group">
          <div
            v-for="(code, index) in redemptionCode"
            :key="index"
            class="code-wrapper"
          >
            <b-form-input
              v-model="redemptionCode[index]"
              maxlength="1"
              class="code-input"
              @paste="handlePaste"
              @input="handleInput(index, $event)"
            />

            <!-- Hyphen after 4th digit -->
            <span
              v-if="index === 3"
              class="divider"
            >
              —
            </span>
          </div>
        </div>
        <div
          v-if="redemptionCodeError"
          class="text-danger text-center mb-1"
          style="font-size: 13px;"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="14"
            class="mr-25"
          />
          {{ redemptionCodeError }}
        </div>

        <!-- Bottom Line -->
        <div class="bottom-line" />

        <!-- Buttons -->
        <div class="d-flex justify-content-center mt-2">
          <b-button
            variant="outline-primary"
            class="mr-1 px-2"
            @click="$bvModal.hide('modal-redemption-code')"
          >
            Cancel
          </b-button>

          <b-button
            variant="primary"
            class="px-2"
            @click="redeemEntitlement"
          >
            Redeem this Entitlement
          </b-button>
        </div>
      </div>
    </b-modal>
    <b-modal
      id="modal-edit-selected-option"
      ref="selected-code-modal"
      centered
      hide-footer
      modal-class="edit-selected-option-modal"
      title="Edit Selected Option"
    >
      <div class="inventory-option-list">
        <div
          v-for="(item, index) in inventoryOptions"
          :key="index"
          class="inventory-option-card"
          :class="{ active: isSelected(item) }"
          @click="selectSingleOption(item)"
        >
          <img
            :src="item.item.image ? item.item.image : require('@/assets/images/placeholder/placeholder_image.png')"
            class="option-image"
          >

          <div class="option-content">
            <h5 class="option-title">
              {{ item.item ? item.item.name : '' }}
            </h5>
            <p class="option-sub">
              {{ item.item ? item.item.sku : '-' }}
            </p>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="$bvModal.hide('modal-edit-selected-option')"
        >
          Cancel
        </b-button>

        <b-button
          variant="success"
          :disabled="selectedOptionTemp.length == 0"
          @click="saveSelectedOption"
        >
          Save Changes
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BTable, BButton, VBTooltip, BBadge, BDropdown, BDropdownItem, BPagination, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BForm, BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store/index'
import Ripple from 'vue-ripple-directive'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
import { required } from '@validations'
import moment from 'moment'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ToastificationContentCustom from '../ToastificationContentCustom.vue'

const { canViewThisAction } = useAclUtils()

export default {
  components: {
    BForm,
    BFormGroup,
    BTable,
    BButton,
    BBadge,
    BDropdown,
    BDropdownItem,
    BRow,
    BCol,
    BFormSelect,
    BPagination,
    BFormTextarea,
    BFormInput,
    BFormCheckbox,
    vSelect,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  props: {
    entitlements: {
      type: [Array, null],
      default: () => [],
    },
  },
  data() {
    return {
      inventoryOptions: [],
      selectedOptionTemp: [],
      schemeOptions: [],
      entitlementID: '',
      currentRedeemId: null,
      redemptionCode: ['', '', '', '', '', '', '', ''],
      redemptionCodeError: '',
      name: '',
      validUntil: '',
      validUntilValidation: false,
      validUntilError: 'Valid status is required',
      internalRemarks: '',
      remarksValidation: false,
      remarksError: 'Internal Remarks is required',
      selectedEntitlement: null,
      user: store.state.auth.userData,
      render: false,
      sortBy: 'stringID',
      sortDesc: false,
      isSortDirDesc: true,
      tableItems: [],
      currentPage: 1,
      totalRows: this.entitlements.length,
      perPage: 10,
      pageOptions: [10, 15, 20],
      statusDataOptions: [
        { title: 'Redeemable', code: 'redeemable' },
        { title: 'Redeemed', code: 'redeemed' },
        { title: 'Used', code: 'used' },
        { title: 'Expired', code: 'expired' },
        { title: 'Cancelled', code: 'cancelled' },
      ],
      flatPickrConfig: {
        wrap: true, enableTime: false, dateFormat: 'd/m/Y', disableMobile: true,
      },
      lifeTime: false,
      validityYear: 3,
      validityType: 'Years',
      validityTypes: [
        { title: 'Months', code: 'Months' },
        { title: 'Years', code: 'Years' },
      ],
      validityYears: Array.from({ length: 9 }, (_, i) => ({
        title: (i + 1).toString(),
        code: i + 1,
      })),
      tableColumns: [
        {
          key: 'stringID',
          label: 'id',
          sortable: true,
        },
        {
          key: 'name',
          label: 'Entitlement',
          sortable: true,
        },
        {
          key: 'fileID',
          label: 'Related file',
          sortable: true,
        },
        {
          key: 'schemeID',
          label: 'Scheme',
          sortable: true,
        },
        {
          key: 'internalRemarks',
          label: 'internal remarks',
          sortable: true,
        },
        {
          key: 'validUntil',
          label: 'valid until',
          sortable: true,
        },
        { key: 'status', sortable: true },
        {
          key: 'createdBy',
          label: 'Created By',
          sortable: true,
        },
        { key: 'actions' },
      ],

      required,
    }
  },
  computed: {
  },

  methods: {
    canViewThisAction,
    isSelected(item) {
      return this.selectedOptionTemp.some(i => (i.inventoryItemId?._id || i.item._id) === item.item._id)
    },
    selectOption(item) {
      const id = item.item._id

      const index = this.selectedOptionTemp.findIndex(i => (i.inventoryItemId?._id || i.item?._id) === id)

      if (index !== -1) {
        // 🔴 already selected → remove (deselect)
        this.selectedOptionTemp.splice(index, 1)
      } else {
        // 🟢 not selected → add
        this.selectedOptionTemp.push(item)
      }
    },
    selectSingleOption(item) {
      const id = item.item._id
      const alreadySelected = this.selectedOptionTemp.some(i => (i.inventoryItemId?._id || i.item?._id) === id)
      this.selectedOptionTemp = alreadySelected ? [] : [item]
    },
    saveSelectedOption() {
      const payload = {
        selectedOptions: this.selectedOptionTemp.map(item => ({
          inventoryItemId: item.item ? item.item._id : item.inventoryItemId._id,
          quantity: item.quantity || 1,
          store: item.store || null,
        })),
      }

      this.$http.patch(
        `customer/entitlements/${this.entitlementID}/selected-options/update`,
        payload,
      )
        .then(() => {
          this.$emit('emitCountEntitlement')
          this.$bvModal.hide('modal-edit-selected-option')
        })
    },
    openEditSelectedOption(item) {
      this.entitlementID = item._id
      this.$http.get(`customer/entitlements/${item._id}/show`)
        .then(response => {
          this.inventoryOptions = response.data.schemeItem?.inventoryOptions ?? []
          this.selectedOptionTemp = response.data.entitlement.selectedOptions.map(i => ({ ...i }))
          this.$bvModal.show('modal-edit-selected-option')
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    proceedToRedeem(id) {
      this.currentRedeemId = id
      this.redemptionCode = ['', '', '', '', '', '', '', '']
      this.redemptionCodeError = ''
      this.$bvModal.show('modal-redemption-code')
    },
    handlePaste(event) {
      event.preventDefault()

      const pastedData = (event.clipboardData || window.clipboardData)
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, 8)

      if (!pastedData) return

      pastedData.split('').forEach((char, index) => {
        this.$set(this.redemptionCode, index, char)
      })
    },

    handleInput(index, value) {
      const cleaned = (value || '').replace(/\D/g, '').slice(0, 1)
      this.$set(this.redemptionCode, index, cleaned)
      if (this.redemptionCodeError) this.redemptionCodeError = ''
      if (cleaned && index < 7) {
        this.$nextTick(() => {
          const inputs = document.querySelectorAll('.code-input')
          if (inputs[index + 1]) inputs[index + 1].focus()
        })
      }
    },
    redeemEntitlement() {
      const code = this.redemptionCode.join('')
      if (code.length < 8) {
        this.redemptionCodeError = 'Please enter the complete 8-digit redemption code.'
        return
      }

      this.redemptionCodeError = ''
      const formData = new FormData()
      formData.append('code', code)

      this.$http.post(`customer/entitlements/${this.currentRedeemId}/finalize-redeem`, formData)
        .then(() => {
          this.$bvModal.hide('modal-redemption-code')
          this.getEntitlementScheme()
          this.$swal({
            title: 'Item has been redeemed',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/save.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          this.redemptionCodeError = error.data?.message ?? 'Invalid redemption code. Please try again.'
        })

      // Example: 12345678
    },
    undoRedemption(id) {
      this.$swal({
        title: 'Undo Redemption?',
        html: 'Undoing this redemption will also cancel the associated Inventory Request. This action cannot be undone. Do you want to proceed?',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning.png'),
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'Go back',
        confirmButtonText: 'Undo Redemption',
        customClass: {
          confirmButton: 'btn btn-primary ml-1',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
      })
        .then(async result => {
          if (result.value) {
            this.updateStatus(id, 'redeemable', true)
          }
        })
    },
    markAsRedeemable(id) {
      this.$swal({
        title: 'Mark as Redeemable?',
        html: 'Are you sure you want to mark this item as redeemable? Once enabled, users may proceed with redemption.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning.png'),
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Yes, Mark as Redeemable',
        customClass: {
          confirmButton: 'btn btn-primary ml-1',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
      })
        .then(async result => {
          if (result.value) {
            this.updateStatus(id, 'redeemable')
          }
        })
    },
    markAsRedeemed(id) {
      this.$swal({
        title: 'Mark as Redeemed?',
        html: 'Are you sure you want to redeem this entitlement? This action cannot be undone.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/success-square.png'),
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'Go back',
        confirmButtonText: 'Yes, Mark as Redeemed',
        customClass: {
          confirmButton: 'btn btn-primary ml-1',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
        width: 475,
      })
        .then(async result => {
          if (result.value) {
            this.updateStatus(id, 'redeemed')
          }
        })
    },
    markAsCancelled(id) {
      this.$swal({
        title: 'Mark as Cancelled?',
        html: 'Are you sure you want to cancel this entitlement? This action cannot be undone.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning-square.png'),
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'Go back',
        confirmButtonText: 'Yes, Mark as Cancelled',
        customClass: {
          confirmButton: 'btn btn-primary ml-1',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
        width: 475,
      })
        .then(async result => {
          if (result.value) {
            this.updateStatus(id, 'cancelled')
          }
        })
    },
    resolveEntitlementStatusVariant(status) {
      if (status === 'redeemable') return 'success'
      if (status === 'redeemed') return 'primary'
      if (status === 'used') return 'primary'
      if (status === 'expired') return 'secondary'
      if (status === 'cancelled') return 'danger'
      return 'primary'
    },
    resolveStatusTypeLabel(statusType, statusReference) {
      const map = {
        customer_app: 'via Customer App',
        code: 'via Code',
        service_request: `via ${statusReference ?? 'SR'}`,
        manual: 'via Manual',
        system: 'via System',
      }
      return map[statusType] ?? statusType
    },
    openTermsModal(item) {
      this.selectedEntitlement = item
    },
    resolveSchemeName(item) {
      // eslint-disable-next-line eqeqeq
      const index = this.schemeOptions.find(i => i._id == item)
      if (index) {
        return index.name
      }

      return ''
    },
    formatCondition(condition) {
      const { condition: title, startDate, endDate } = condition

      const start = startDate ? moment(startDate, 'DD/MM/YYYY').format('DD/MM/YYYY') : null
      const end = endDate ? moment(endDate, 'DD/MM/YYYY').format('DD/MM/YYYY') : null

      if (start && end) return `${title} ${start} to ${end}`
      if (start) return `${title} from ${start}`
      if (end) return `${title} until ${end}`

      return title
    },
    hideFilter() {
      this.$refs.filter_dropdown_all_entitlement.hide(true)
    },
    showFilter() {
      this.$refs.filter_dropdown_all_entitlement.show(true)
    },
    removeFromStatusFilter(item) {
      const index = this.statusFilter.indexOf(item)
      if (index > -1) {
        this.statusFilter.splice(index, 1)
      }
    },
    removeFromTypeFilter(item) {
      const index = this.typeFilter.indexOf(item)
      if (index > -1) {
        this.typeFilter.splice(index, 1)
      }
    },
    removeFromPaidEntitlementFilter(item) {
      const index = this.paidEntitlementFilter.indexOf(item)
      if (index > -1) {
        this.paidEntitlementFilter.splice(index, 1)
      }
    },
    removeFromSchemeFilter(item) {
      const index = this.schemeFilter.indexOf(item)
      if (index > -1) {
        this.schemeFilter.splice(index, 1)
      }
    },

    clearAllFilters() {
      this.startDateFilter = ''
      this.statusFilter = []
      this.typeFilter = []
      this.paidEntitlementFilter = []
      this.schemeFilter = []
    },
    setEntitlement(item) {
      this.entitlementID = item._id
      this.validUntil = item.validUntil
      this.name = item.name
    },
    setEntitlementRemarks(item) {
      this.entitlementID = item._id
      this.internalRemarks = item.internalRemarks
      this.name = item.name
    },
    submitForm() {
      this.$refs.entitlementValidUntilEditForm.validate().then(success => {
        if (success) {
          const formData = new FormData()
          formData.append('lifeTime', this.lifeTime)
          formData.append('validityYear', this.validityYear)
          formData.append('validityType', this.validityType)

          this.$http.patch(`customer/entitlements/${this.entitlementID}/validity/update`, formData)
            .then(() => {
              this.$emit('emitCountEntitlement')
              this.$swal({
                title: 'Validity Updated!',
                html: `The validity for <strong>'${this.name}'</strong> has been updated`,
                // eslint-disable-next-line global-require
                imageUrl: require('@/assets/images/icons/save.png'),
                imageWidth: 80,
                imageHeight: 80,
                imageAlt: 'Custom Icon',
                showCancelButton: false,
                confirmButtonText: 'Okay',
                customClass: {
                  confirmButton: 'btn btn-primary',
                },
                buttonsStyling: false,
              })
            })
            .catch(error => {
              if (error.data.errors) {
                error.data.errors.forEach(validationError => {
                  if (validationError.param === 'validUntil') {
                    this.validUntilError = validationError.msg
                    this.validUntilValidation = true
                  }
                })
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
        }
      })
    },
    sendEmail(entitlement) {
      if (entitlement.customerID && entitlement.customerID.perEmail === null) {
        this.$swal({
          // eslint-disable-next-line global-require
          title: 'No email address found', html: 'The customer has not provided an email address.', imageUrl: require('@/assets/images/icons/warning.png'), imageWidth: 80, imageHeight: 80, imageAlt: 'Custom Icon', showCancelButton: false, confirmButtonText: 'Okay', customClass: { confirmButton: 'btn btn-primary' }, buttonsStyling: false,
        })
        return
      }
      this.$swal({
        // eslint-disable-next-line global-require
        title: 'Re-send Entitlement Email?', html: `An email containing the entitlement details and current status will be sent to ${entitlement.customerID ? entitlement.customerID.perEmail : '-'}.`, imageUrl: require('@/assets/images/icons/mail.png'), reverseButtons: true, showCancelButton: true, cancelButtonText: 'Go back', confirmButtonText: 'Send Email', customClass: { confirmButton: 'btn btn-primary ml-1', cancelButton: 'btn btn-outline-primary' }, buttonsStyling: false,
      })
        .then(async result => {
          if (result.value) {
            this.$http.post(`customer/entitlements/${entitlement._id}/send-email`)
              .then(() => {
                this.$swal({
                  // eslint-disable-next-line global-require
                  title: 'The entitlement status email has been sent', imageUrl: require('@/assets/images/icons/success.png'), imageWidth: 80, imageHeight: 80, imageAlt: 'Custom Icon', showCancelButton: false, showConfirmButton: false, buttonsStyling: false,
                })
              })
          }
        })
    },
    updateStatus(entitlement, status, undo = false) {
      const formData = new FormData()
      formData.append('status', status)
      this.$http.patch(`customer/entitlements/${entitlement._id}/status/update`, formData)
        .then(() => {
          this.$emit('emitCountEntitlement')
          if (status === 'redeemed') {
            const toastMessage = `<span class="text-bold-black">${entitlement.stringID} - ${entitlement.name}</span> has been mark as redeemed successfully.`
            this.$toast({
              component: ToastificationContentCustom,
              props: {
                title: 'Mark as Redeemed', text: toastMessage, icon: 'CheckCircleIcon', variant: 'success', hideClose: true,
              },
            })
          } else if (status === 'cancelled') {
            const toastMessage = `<span class="text-bold-black">${entitlement.stringID} - ${entitlement.name}</span> has been mark as cancelled successfully.`
            this.$toast({
              component: ToastificationContentCustom,
              props: {
                title: 'Mark as Cancelled', text: toastMessage, icon: 'CheckCircleIcon', variant: 'success', hideClose: true,
              },
            })
          } else if (status === 'redeemable' && !undo) {
            const toastMessage = `<span class="text-bold-black">${entitlement.stringID} - ${entitlement.name}</span> has been mark as redeemable successfully.`
            this.$toast({
              component: ToastificationContentCustom,
              props: {
                title: 'Mark as Redeemable', text: toastMessage, icon: 'CheckCircleIcon', variant: 'success', hideClose: true,
              },
            })
          } else if (status === 'redeemable' && undo) {
            this.$swal({
              // eslint-disable-next-line global-require
              title: 'Redemption has been reverted.', imageUrl: require('@/assets/images/icons/success.png'), imageWidth: 80, imageHeight: 80, imageAlt: 'Custom Icon', showCancelButton: false, showConfirmButton: false, buttonsStyling: false,
            })
          }
        })
        .catch(error => {
          if (error.data.status === 'collected') {
            this.$swal({
              // eslint-disable-next-line global-require
              title: 'Unable to Undo Redemption', html: 'This entitlement has already been collected and its redemption can no longer be reversed.', imageUrl: require('@/assets/images/icons/alert-red.png'), imageWidth: 80, imageHeight: 80, imageAlt: 'Custom Icon', showCancelButton: false, confirmButtonText: 'Close', customClass: { confirmButton: 'btn btn-primary' }, buttonsStyling: false,
            })
          } else {
            this.$toast({ component: ToastificationContent, props: { title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE, icon: 'AlertTriangleIcon', variant: 'danger' } })
          }
        })
    },
    submitRemarksForm() {
      this.$refs.entitlementRemarksEditForm.validate().then(success => {
        if (success) {
          const formData = new FormData()
          formData.append('internalRemarks', this.internalRemarks)

          this.$http.patch(`customer/entitlements/${this.entitlementID}/internal-remarks/update`, formData)
            .then(() => {
              this.$emit('emitCountEntitlement')
              this.$swal({
                title: 'Internal Remarks Updated!',
                html: `The internal remarks for <strong>'${this.name}'</strong> has been updated`,
                // eslint-disable-next-line global-require
                imageUrl: require('@/assets/images/icons/save.png'),
                imageWidth: 80,
                imageHeight: 80,
                imageAlt: 'Custom Icon',
                showCancelButton: false,
                confirmButtonText: 'Okay',
                customClass: {
                  confirmButton: 'btn btn-primary',
                },
                buttonsStyling: false,
              })
            })
            .catch(error => {
              if (error.data.errors) {
                error.data.errors.forEach(validationError => {
                  if (validationError.param === 'internalRemarks') {
                    this.remarksError = validationError.msg
                    this.remarksValidation = true
                  }
                })
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
        }
      })
    },
  },
}
</script>

<style lang="scss">
.redemption-code-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
}

.code-input {
  width: 48px;
  height: 48px;
  text-align: center;
  font-size: 20px;
  border-radius: 6px;
  border: 1px solid #d8d8d8;

  &:focus {
    border-color: #0052cc;
    box-shadow: none;
  }
}
.code-wrapper {
  display: flex;
  align-items: center;
}
.clock-icon {
  color: #f59e0b;
}
.redemption-wrapper {
  position: relative;
}

.modal-close-btn,
.close {
  position: relative;
  z-index: 9999;
}
// .inventory-option-card.active {
//   border: 2px solid #28c76f;
//   background-color: rgba(40, 199, 111, 0.1);
// }
</style>
